import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button, FormControl,
    InputLabel, Select, MenuItem, Chip, Stack, Divider, LinearProgress,
    Stepper, Step, StepLabel, StepContent, Paper, Avatar, 
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    useTheme, alpha
} from '@mui/material';
import {
    Psychology as PsychologyIcon, FindInPage as FindInPageIcon, Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon, Save as SaveIcon, Groups as GroupsIcon,
    Security as SecurityIcon, TrendingUp as TrendingUpIcon,
    PlaylistAddCheck as PlaylistAddCheckIcon, Check as CheckIcon, Close as CloseIcon,
    Fullscreen as FullscreenIcon, Timeline as TimelineIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../../../supabaseClient';
import {
    D1TeamFormation,
    D2ProblemDescription, 
    D3InterimContainment,
    D4RootCauseAnalysis,
    D5CorrectiveActions,
    D6Implementation,
    D7Prevention,
    D8Recognition
} from './root-cause-investigation-components';

const d8Methodology = [
    { id: 'd1', title: 'D1: Team Formation', description: 'Establish cross-functional team', icon: <GroupsIcon />, color: '#ff9800' },
    { id: 'd2', title: 'D2: Problem Description', description: 'Define problem quantifiably', icon: <AssignmentIcon />, color: '#ff9800' },
    { id: 'd3', title: 'D3: Interim Containment', description: 'Implement protective actions', icon: <SecurityIcon />, color: '#f44336' },
    { id: 'd4', title: 'D4: Root Cause Analysis', description: 'Identify and verify root cause', icon: <PsychologyIcon />, color: '#ff5722' },
    { id: 'd5', title: 'D5: Corrective Actions', description: 'Choose and verify solutions', icon: <PlaylistAddCheckIcon />, color: '#4caf50' },
    { id: 'd6', title: 'D6: Implementation', description: 'Implement permanent actions', icon: <TimelineIcon />, color: '#607d8b' },
    { id: 'd7', title: 'D7: Prevention', description: 'Prevent recurrence', icon: <TrendingUpIcon />, color: '#795548' },
    { id: 'd8', title: 'D8: Team Recognition', description: 'Recognize team contributions', icon: <CheckCircleIcon />, color: '#388e3c' }
];



export default function RootCauseInvestigation({ batchLogId, showSnackbar, onInvestigationComplete, onInvestigationReopened, canEdit = false, canStartCAPA = false, canCloseInvestigations = false, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [deviations, setDeviations] = useState([]);
    const [completedDeviations, setCompletedDeviations] = useState([]);
    const [selectedDeviation, setSelectedDeviation] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isReadOnly, setIsReadOnly] = useState(false);

    const [teamDialogOpen, setTeamDialogOpen] = useState(false);
    const [pendingPage, setPendingPage] = useState(1);
    const [completedPage, setCompletedPage] = useState(1);
    const [fullScreenOpen, setFullScreenOpen] = useState(false);

    const [d8Data, setD8Data] = useState({
        d1_team_formation: { 
            // Team Lead Information
            lead: '',
            leadDepartment: '',
            leadContact: '',
            // Team Members
            members: [],
            // Team Charter & Authority
            scope: '',
            authorityLevel: '',
            successCriteria: '',
            // Resource Allocation
            budget: '',
            timeAllocation: '',
            equipmentAccess: [],
            customEquipment: '',
            // Coverage Areas
            coverage_process_knowledge: false,
            coverage_quality_compliance: false,
            coverage_technical_engineering: false,
            coverage_management_authority: false,
            coverage_operations: false,
            coverage_subject_matter_expert: false,
            // Status
            completed: false 
        },
        d2_problem_description: { 
            // 5W2H Analysis
            what: '',
            who: '',
            when: '',
            where: '',
            how: '',
            howMuch: '',
            // Impact Assessment
            severityLevel: '',
            urgencyLevel: '',
            customerImpact: '',
            // Financial Impact
            costImpact: '',
            batchValue: '',
            recoveryCosts: '',
            lostRevenue: '',
            financialPriority: '',
            insuranceCoverage: '',
            costCategories: [],
            // Affected Products & Regulatory
            affectedProductsData: [],
            regulatoryRisk: '',
            recallPotential: '',
            // Evidence & Documentation
            evidenceTypes: [],
            evidenceFiles: [],
            evidenceLocation: '',
            // Status
            completed: false 
        },
        d3_interim_containment: { 
            // Immediate Actions
            productionStopped: false,
            inventoryIsolated: false,
            customersNotified: false,
            regulatoryNotification: false,
            managementInformed: false,
            // Containment Action Plan
            containmentTypes: [],
            responsiblePerson: '',
            responsibleDepartment: '',
            urgencyLevel: '',
            implementationDate: '',
            actionDetails: '',
            // Effectiveness Verification
            verificationMethods: '',
            effectivenessRating: '',
            // Process & Supply Chain Actions
            supplierActions: [],
            supplierCommunication: '',
            manufacturingControls: [],
            processStatus: '',
            qualityControlMeasures: [],
            testingFrequency: '',
            // Monitoring & Communication Plan
            monitoringMethods: [],
            monitoringResponsibility: '',
            communicationStakeholders: [],
            communicationMethod: '',
            estimatedDuration: '',
            containmentCost: '',
            reviewFrequency: '',
            escalationCriteria: '',
            monitoringNotes: '',
            // Status
            completed: false 
        },
        d4_root_cause_analysis: {
            // 5 Whys Analysis
            fiveWhys: {
                problem: '',
                whys: [{ question: '', answer: '' }]
            },
            // Fishbone Diagram
            fishbone: {
                problem: '',
                categories: {
                    people: [],
                    process: [],
                    materials: [],
                    equipment: [],
                    environment: [],
                    methods: []
                }
            },
            // Change Analysis
            changeAnalysis: {
                timeframe: '30',
                changes: []
            },
            // Evidence Collection
            evidenceCollection: {
                physical: [],
                documentary: [],
                testimonial: [],
                digital: []
            },
            // Additional Analysis
            hypotheses: [],
            rootCause: '',
            verification: '',
            // Status
            completed: false
        },
        d5_corrective_actions: { 
            // Corrective Actions Array
            correctiveActions: [],
            // Resource Allocation
            totalBudget: '',
            totalHours: '',
            startDate: '',
            targetDate: '',
            personnelRequirements: '',
            equipmentMaterials: '',
            externalServices: '',
            trainingNeeds: '',
            dependencies: '',
            budgetBreakdown: '',
            // Risk Assessment Array
            riskAssessments: [],
            // Verification Planning
            verificationMethods: '',
            successCriteria: '',
            dataCollectionPlan: '',
            baselineData: '',
            monitoringPeriod: '',
            verificationOwner: '',
            verificationSchedule: '',
            // Status
            completed: false 
        },
        d6_implementation: { 
            // Strategy & Planning Phase
            implementationStrategy: '',
            rolloutPlan: '',
            resourceAllocation: '',
            stakeholderPlan: '',
            successMetrics: '',
            // Pilot & Validation Phase
            pilotScope: '',
            validationCriteria: '',
            pilotStartDate: '',
            pilotEndDate: '',
            pilotStatus: '',
            pilotResults: '',
            // Full Deployment Phase
            deploymentPlan: '',
            rolloutSchedule: '',
            deploymentStatus: '',
            deploymentProgress: '',
            implementationActions: [],
            // Training & Change Management Phase
            changeManagementPlan: '',
            communicationPlan: '',
            trainingPrograms: [],
            // Monitoring & Control Phase
            monitoringPlan: '',
            kpiDefinitions: '',
            controlMechanisms: '',
            reportingPlan: '',
            // Compliance & Sustainability Phase
            complianceValidation: '',
            sustainabilityPlan: '',
            documentationStatus: '',
            effectivenessRating: '',
            implementationSummary: '',
            // Status
            completed: false 
        },
        d7_prevention_plan: { 
            // Prevention Strategy Phase
            preventionSummary: '',
            keyChanges: '',
            expectedOutcome: '',
            preventionScope: '',
            resourceRequirements: '',
            // Detection Systems Phase
            detectionMethod: '',
            monitoringFrequency: '',
            alertCriteria: '',
            responseProcedures: '',
            // Implementation Timeline Phase
            implementationDate: '',
            projectOwner: '',
            timelineItems: [],
            // Risk Mitigation Phase
            riskMitigationPlan: '',
            contingencyPlans: '',
            riskMonitoring: '',
            riskCommunication: '',
            // Monitoring & Control Phase
            monitoringPlan: '',
            controlMechanisms: '',
            reportingStructure: '',
            escalationProcedures: '',
            // Effectiveness Review Phase
            successMeasures: '',
            reviewSchedule: '',
            improvementPlan: '',
            lessonsLearned: '',
            // Prevention Actions Array
            preventionActions: [],
            // Status
            completed: false 
        },
        d8_team_recognition: { 
            // Team Recognition Phase
            teamSummary: '',
            keyContributors: '',
            teamStrengths: '',
            teamMembers: [],
            // Lessons Learned Phase
            criticalLessons: '',
            processImprovements: '',
            lessonsLearned: [],
            // Knowledge Sharing Phase
            sharingPlan: '',
            documentationPlan: '',
            trainingPlan: '',
            // Success Metrics Phase
            successMetrics: '',
            impactAssessment: '',
            futurePreventionValue: '',
            // Celebration Phase
            celebrationPlan: '',
            communicationPlan: '',
            finalSummary: '',
            // Status
            completed: false 
        }
    });

    const [newMember, setNewMember] = useState({});
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [companyId, setCompanyId] = useState(null);

    useEffect(() => {
        loadPendingDeviations();
        loadCompletedDeviations();
        loadProducts();
        loadCompanyId();
    }, []);

    const loadCompanyId = async () => {
        try {
            const { data, error } = await supabase
                .from('companies')
                .select('id')
                .single();

            if (error) {
                console.error('Error loading company ID:', error);
                return;
            }

            setCompanyId(data.id);
        } catch (error) {
            console.error('Error loading company ID:', error);
        }
    };

    const loadProducts = async () => {
        setLoadingProducts(true);
        try {
            const { data: productsData, error: productsError } = await supabase
                .from('products')
                .select('id, name, sku, product_code, has_variants, variants')
                .eq('is_active', true)
                .order('name', { ascending: true });

            if (productsError) {
                console.error('Error loading products:', productsError);
                return;
            }

            const { data: variantsData, error: variantsError } = await supabase
                .from('product_variants')
                .select('id, product_id, name, sku, barcode, pack_quantity')
                .eq('is_active', true)
                .order('name', { ascending: true });

            if (variantsError) {
                console.error('Error loading product variants:', variantsError);
            }

            const allProductOptions = [];
            
            (productsData || []).forEach(product => {
                allProductOptions.push({
                    id: product.id,
                    type: 'product',
                    name: product.name,
                    sku: product.sku,
                    product_code: product.product_code,
                    displayName: product.name,
                    searchText: `${product.name} ${product.sku || ''} ${product.product_code || ''}`.toLowerCase()
                });

                if (product.has_variants && product.variants) {
                    try {
                        const jsonVariants = Array.isArray(product.variants) ? product.variants : JSON.parse(product.variants);
                        jsonVariants.forEach((variant, index) => {
                            allProductOptions.push({
                                id: `${product.id}_variant_${index}`,
                                type: 'variant_json',
                                productId: product.id,
                                parentName: product.name,
                                name: variant.name || `Variant ${index + 1}`,
                                sku: variant.sku,
                                pack_quantity: variant.pack_quantity,
                                displayName: `${product.name} - ${variant.name || `Variant ${index + 1}`}`,
                                searchText: `${product.name} ${variant.name || ''} ${variant.sku || ''}`.toLowerCase()
                            });
                        });
                    } catch (e) {
                        console.error('Error parsing JSON variants for product:', product.id, e);
                    }
                }
            });

            (variantsData || []).forEach(variant => {
                const parentProduct = (productsData || []).find(p => p.id === variant.product_id);
                if (parentProduct) {
                    allProductOptions.push({
                        id: variant.id,
                        type: 'variant_table',
                        productId: variant.product_id,
                        parentName: parentProduct.name,
                        name: variant.name,
                        sku: variant.sku,
                        barcode: variant.barcode,
                        pack_quantity: variant.pack_quantity,
                        displayName: `${parentProduct.name} - ${variant.name}`,
                        searchText: `${parentProduct.name} ${variant.name} ${variant.sku || ''} ${variant.barcode || ''}`.toLowerCase()
                    });
                }
            });

            allProductOptions.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setProducts(allProductOptions);

        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoadingProducts(false);
        }
    };



    const loadPendingDeviations = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('batch_log_deviations')
                .select('*')
                .in('status', ['investigating', 'capa_required'])
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error loading deviations:', error);
                return;
            }

            setDeviations(data || []);
        } catch (error) {
            console.error('Error loading deviations:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCompletedDeviations = async () => {
        try {
            const { data, error } = await supabase
                .from('batch_log_deviations')
                .select('*')
                .eq('status', 'closed_with_capa')
                .order('investigation_completed_at', { ascending: false });

            if (error) {
                console.error('Error loading completed deviations:', error);
                return;
            }

            setCompletedDeviations(data || []);
        } catch (error) {
            console.error('Error loading completed deviations:', error);
        }
    };

    const handleDeviationSelect = async (deviation) => {
        setSelectedDeviation(deviation);
        setActiveStep(0);
        setFullScreenOpen(true);
        setIsReadOnly(false);
        
        // Auto-transition from 'capa_required' to 'investigating' when investigation starts
        if (deviation.status === 'capa_required') {
            try {
                const { error } = await supabase
                    .from('batch_log_deviations')
                    .update({ 
                        status: 'investigating',
                        investigation_started_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', deviation.id);

                if (error) {
                    console.error('Error starting investigation:', error);
                    showSnackbar?.('Error starting investigation: ' + error.message, 'error');
                } else {
                    // Update local state
                    setSelectedDeviation({ ...deviation, status: 'investigating' });
                    showSnackbar?.('Investigation started successfully', 'success');
                    loadPendingDeviations(); // Refresh the list
                }
            } catch (error) {
                console.error('Error starting investigation:', error);
                showSnackbar?.('Error starting investigation: ' + error.message, 'error');
            }
        }
        
        // Load existing investigation data if available
        if (deviation.d1_team_formation) {
            setD8Data({
                d1_team_formation: deviation.d1_team_formation || { 
                    lead: '',
                    leadDepartment: '',
                    leadContact: '',
                    members: [],
                    scope: '',
                    authorityLevel: '',
                    successCriteria: '',
                    budget: '',
                    timeAllocation: '',
                    equipmentAccess: [],
                    customEquipment: '',
                    coverage_process_knowledge: false,
                    coverage_quality_compliance: false,
                    coverage_technical_engineering: false,
                    coverage_management_authority: false,
                    coverage_operations: false,
                    coverage_subject_matter_expert: false,
                    completed: false 
                },
                d2_problem_description: deviation.d2_problem_description || { 
                    what: '',
                    who: '',
                    when: '',
                    where: '',
                    how: '',
                    howMuch: '',
                    severityLevel: '',
                    urgencyLevel: '',
                    customerImpact: '',
                    costImpact: '',
                    batchValue: '',
                    recoveryCosts: '',
                    lostRevenue: '',
                    financialPriority: '',
                    insuranceCoverage: '',
                    costCategories: [],
                    affectedProductsData: [],
                    regulatoryRisk: '',
                    recallPotential: '',
                    evidenceTypes: [],
                    evidenceFiles: [],
                    evidenceLocation: '',
                    completed: false 
                },
                d3_interim_containment: deviation.d3_interim_containment || { 
                    // Immediate Actions
                    productionStopped: false,
                    inventoryIsolated: false,
                    customersNotified: false,
                    regulatoryNotification: false,
                    managementInformed: false,
                    // Containment Action Plan
                    containmentTypes: [],
                    responsiblePerson: '',
                    responsibleDepartment: '',
                    urgencyLevel: '',
                    implementationDate: '',
                    actionDetails: '',
                    // Effectiveness Verification
                    verificationMethods: '',
                    effectivenessRating: '',
                    // Process & Supply Chain Actions
                    supplierActions: [],
                    supplierCommunication: '',
                    manufacturingControls: [],
                    processStatus: '',
                    qualityControlMeasures: [],
                    testingFrequency: '',
                    // Monitoring & Communication Plan
                    monitoringMethods: [],
                    monitoringResponsibility: '',
                    communicationStakeholders: [],
                    communicationMethod: '',
                    estimatedDuration: '',
                    containmentCost: '',
                    reviewFrequency: '',
                    escalationCriteria: '',
                    monitoringNotes: '',
                    // Status
                    completed: false 
                },
                d4_root_cause_analysis: deviation.d4_root_cause_analysis || { methods: [], findings: '', completed: false },
                d5_corrective_actions: deviation.d5_corrective_actions || { 
                    correctiveActions: [],
                    totalBudget: '',
                    totalHours: '',
                    startDate: '',
                    targetDate: '',
                    personnelRequirements: '',
                    equipmentMaterials: '',
                    externalServices: '',
                    trainingNeeds: '',
                    dependencies: '',
                    budgetBreakdown: '',
                    riskAssessments: [],
                    verificationMethods: '',
                    successCriteria: '',
                    dataCollectionPlan: '',
                    baselineData: '',
                    monitoringPeriod: '',
                    verificationOwner: '',
                    verificationSchedule: '',
                    completed: false 
                },
                d6_implementation: deviation.d6_implementation || { 
                    implementationStrategy: '',
                    rolloutPlan: '',
                    resourceAllocation: '',
                    stakeholderPlan: '',
                    successMetrics: '',
                    pilotScope: '',
                    validationCriteria: '',
                    pilotStartDate: '',
                    pilotEndDate: '',
                    pilotStatus: '',
                    pilotResults: '',
                    deploymentPlan: '',
                    rolloutSchedule: '',
                    deploymentStatus: '',
                    deploymentProgress: '',
                    implementationActions: [],
                    changeManagementPlan: '',
                    communicationPlan: '',
                    trainingPrograms: [],
                    monitoringPlan: '',
                    kpiDefinitions: '',
                    controlMechanisms: '',
                    reportingPlan: '',
                    complianceValidation: '',
                    sustainabilityPlan: '',
                    documentationStatus: '',
                    effectivenessRating: '',
                    implementationSummary: '',
                    completed: false 
                },
                d7_prevention_plan: deviation.d7_prevention_plan || { 
                    preventionSummary: '',
                    keyChanges: '',
                    expectedOutcome: '',
                    preventionScope: '',
                    resourceRequirements: '',
                    detectionMethod: '',
                    monitoringFrequency: '',
                    alertCriteria: '',
                    responseProcedures: '',
                    implementationDate: '',
                    projectOwner: '',
                    timelineItems: [],
                    riskMitigationPlan: '',
                    contingencyPlans: '',
                    riskMonitoring: '',
                    riskCommunication: '',
                    monitoringPlan: '',
                    controlMechanisms: '',
                    reportingStructure: '',
                    escalationProcedures: '',
                    successMeasures: '',
                    reviewSchedule: '',
                    improvementPlan: '',
                    lessonsLearned: '',
                    preventionActions: [],
                    completed: false 
                },
                d8_team_recognition: deviation.d8_team_recognition || { 
                    teamSummary: '',
                    keyContributors: '',
                    teamStrengths: '',
                    teamMembers: [],
                    criticalLessons: '',
                    processImprovements: '',
                    lessonsLearned: [],
                    sharingPlan: '',
                    documentationPlan: '',
                    trainingPlan: '',
                    successMetrics: '',
                    impactAssessment: '',
                    futurePreventionValue: '',
                    celebrationPlan: '',
                    communicationPlan: '',
                    finalSummary: '',
                    completed: false 
                }
            });
        } else {
            // Initialize fresh 8D data structure for new investigations
            setD8Data({
                d1_team_formation: { 
                    lead: '',
                    leadDepartment: '',
                    leadContact: '',
                    members: [],
                    scope: '',
                    authorityLevel: '',
                    successCriteria: '',
                    budget: '',
                    timeAllocation: '',
                    equipmentAccess: [],
                    customEquipment: '',
                    coverage_process_knowledge: false,
                    coverage_quality_compliance: false,
                    coverage_technical_engineering: false,
                    coverage_management_authority: false,
                    coverage_operations: false,
                    coverage_subject_matter_expert: false,
                    completed: false 
                },
                d2_problem_description: { 
                    what: '',
                    who: '',
                    when: '',
                    where: '',
                    how: '',
                    howMuch: '',
                    severityLevel: '',
                    urgencyLevel: '',
                    customerImpact: '',
                    costImpact: '',
                    batchValue: '',
                    recoveryCosts: '',
                    lostRevenue: '',
                    financialPriority: '',
                    insuranceCoverage: '',
                    costCategories: [],
                    affectedProductsData: [],
                    regulatoryRisk: '',
                    recallPotential: '',
                    evidenceTypes: [],
                    evidenceFiles: [],
                    evidenceLocation: '',
                    completed: false 
                },
                d3_interim_containment: { 
            // Immediate Actions
            productionStopped: false,
            inventoryIsolated: false,
            customersNotified: false,
            regulatoryNotification: false,
            managementInformed: false,
            // Containment Action Plan
            containmentTypes: [],
            responsiblePerson: '',
            responsibleDepartment: '',
            urgencyLevel: '',
            implementationDate: '',
            actionDetails: '',
            // Effectiveness Verification
            verificationMethods: '',
            effectivenessRating: '',
            // Process & Supply Chain Actions
            supplierActions: [],
            supplierCommunication: '',
            manufacturingControls: [],
            processStatus: '',
            qualityControlMeasures: [],
            testingFrequency: '',
            // Monitoring & Communication Plan
            monitoringMethods: [],
            monitoringResponsibility: '',
            communicationStakeholders: [],
            communicationMethod: '',
            estimatedDuration: '',
            containmentCost: '',
            reviewFrequency: '',
            escalationCriteria: '',
            monitoringNotes: '',
            // Status
            completed: false 
        },
                d4_root_cause_analysis: {
            // 5 Whys Analysis
            fiveWhys: {
                problem: '',
                whys: [{ question: '', answer: '' }]
            },
            // Fishbone Diagram
            fishbone: {
                problem: '',
                categories: {
                    people: [],
                    process: [],
                    materials: [],
                    equipment: [],
                    environment: [],
                    methods: []
                }
            },
            // Change Analysis
            changeAnalysis: {
                timeframe: '30',
                changes: []
            },
            // Evidence Collection
            evidenceCollection: {
                physical: [],
                documentary: [],
                testimonial: [],
                digital: []
            },
            // Additional Analysis
            hypotheses: [],
            rootCause: '',
            verification: '',
            // Status
            completed: false
        },
                d5_corrective_actions: { 
                    correctiveActions: [],
                    totalBudget: '',
                    totalHours: '',
                    startDate: '',
                    targetDate: '',
                    personnelRequirements: '',
                    equipmentMaterials: '',
                    externalServices: '',
                    trainingNeeds: '',
                    dependencies: '',
                    budgetBreakdown: '',
                    riskAssessments: [],
                    verificationMethods: '',
                    successCriteria: '',
                    dataCollectionPlan: '',
                    baselineData: '',
                    monitoringPeriod: '',
                    verificationOwner: '',
                    verificationSchedule: '',
                    completed: false 
                },
                d6_implementation: { 
                    implementationStrategy: '',
                    rolloutPlan: '',
                    resourceAllocation: '',
                    stakeholderPlan: '',
                    successMetrics: '',
                    pilotScope: '',
                    validationCriteria: '',
                    pilotStartDate: '',
                    pilotEndDate: '',
                    pilotStatus: '',
                    pilotResults: '',
                    deploymentPlan: '',
                    rolloutSchedule: '',
                    deploymentStatus: '',
                    deploymentProgress: '',
                    implementationActions: [],
                    changeManagementPlan: '',
                    communicationPlan: '',
                    trainingPrograms: [],
                    monitoringPlan: '',
                    kpiDefinitions: '',
                    controlMechanisms: '',
                    reportingPlan: '',
                    complianceValidation: '',
                    sustainabilityPlan: '',
                    documentationStatus: '',
                    effectivenessRating: '',
                    implementationSummary: '',
                    completed: false 
                },
                d7_prevention_plan: { 
                    preventionSummary: '',
                    keyChanges: '',
                    expectedOutcome: '',
                    preventionScope: '',
                    resourceRequirements: '',
                    detectionMethod: '',
                    monitoringFrequency: '',
                    alertCriteria: '',
                    responseProcedures: '',
                    implementationDate: '',
                    projectOwner: '',
                    timelineItems: [],
                    riskMitigationPlan: '',
                    contingencyPlans: '',
                    riskMonitoring: '',
                    riskCommunication: '',
                    monitoringPlan: '',
                    controlMechanisms: '',
                    reportingStructure: '',
                    escalationProcedures: '',
                    successMeasures: '',
                    reviewSchedule: '',
                    improvementPlan: '',
                    lessonsLearned: '',
                    preventionActions: [],
                    completed: false 
                },
                d8_team_recognition: { 
                    teamSummary: '',
                    keyContributors: '',
                    teamStrengths: '',
                    teamMembers: [],
                    criticalLessons: '',
                    processImprovements: '',
                    lessonsLearned: [],
                    sharingPlan: '',
                    documentationPlan: '',
                    trainingPlan: '',
                    successMetrics: '',
                    impactAssessment: '',
                    futurePreventionValue: '',
                    celebrationPlan: '',
                    communicationPlan: '',
                    finalSummary: '',
                    completed: false 
                }
            });
        }
        
        
    };

    const handleCompletedDeviationSelect = (deviation) => {
        setSelectedDeviation(deviation);
        setActiveStep(0);
        setFullScreenOpen(true);
        setIsReadOnly(true);
        
        // Load existing investigation data
        setD8Data({
            d1_team_formation: deviation.d1_team_formation || { 
                lead: '',
                leadDepartment: '',
                leadContact: '',
                members: [],
                scope: '',
                authorityLevel: '',
                successCriteria: '',
                budget: '',
                timeAllocation: '',
                equipmentAccess: [],
                customEquipment: '',
                coverage_process_knowledge: false,
                coverage_quality_compliance: false,
                coverage_technical_engineering: false,
                coverage_management_authority: false,
                coverage_operations: false,
                coverage_subject_matter_expert: false,
                completed: false 
            },
            d2_problem_description: deviation.d2_problem_description || { 
                what: '',
                who: '',
                when: '',
                where: '',
                how: '',
                howMuch: '',
                severityLevel: '',
                urgencyLevel: '',
                customerImpact: '',
                costImpact: '',
                batchValue: '',
                recoveryCosts: '',
                lostRevenue: '',
                financialPriority: '',
                insuranceCoverage: '',
                costCategories: [],
                affectedProductsData: [],
                regulatoryRisk: '',
                recallPotential: '',
                evidenceTypes: [],
                evidenceFiles: [],
                evidenceLocation: '',
                completed: false 
            },
            d3_interim_containment: deviation.d3_interim_containment || { 
                productionStopped: false,
                inventoryIsolated: false,
                customersNotified: false,
                regulatoryNotification: false,
                managementInformed: false,
                containmentTypes: [],
                responsiblePerson: '',
                responsibleDepartment: '',
                urgencyLevel: '',
                implementationDate: '',
                actionDetails: '',
                verificationMethods: '',
                effectivenessRating: '',
                supplierActions: [],
                supplierCommunication: '',
                manufacturingControls: [],
                processStatus: '',
                qualityControlMeasures: [],
                testingFrequency: '',
                monitoringMethods: [],
                monitoringResponsibility: '',
                communicationStakeholders: [],
                communicationMethod: '',
                estimatedDuration: '',
                containmentCost: '',
                reviewFrequency: '',
                escalationCriteria: '',
                monitoringNotes: '',
                completed: false 
            },
            d4_root_cause_analysis: deviation.d4_root_cause_analysis || { methods: [], findings: '', completed: false },
            d5_corrective_actions: deviation.d5_corrective_actions || { 
                correctiveActions: [],
                totalBudget: '',
                totalHours: '',
                startDate: '',
                targetDate: '',
                personnelRequirements: '',
                equipmentMaterials: '',
                externalServices: '',
                trainingNeeds: '',
                dependencies: '',
                budgetBreakdown: '',
                riskAssessments: [],
                verificationMethods: '',
                successCriteria: '',
                dataCollectionPlan: '',
                baselineData: '',
                monitoringPeriod: '',
                verificationOwner: '',
                verificationSchedule: '',
                completed: false 
            },
            d6_implementation: deviation.d6_implementation || { 
                implementationStrategy: '',
                rolloutPlan: '',
                resourceAllocation: '',
                stakeholderPlan: '',
                successMetrics: '',
                pilotScope: '',
                validationCriteria: '',
                pilotStartDate: '',
                pilotEndDate: '',
                pilotStatus: '',
                pilotResults: '',
                deploymentPlan: '',
                rolloutSchedule: '',
                deploymentStatus: '',
                deploymentProgress: '',
                implementationActions: [],
                changeManagementPlan: '',
                communicationPlan: '',
                trainingPrograms: [],
                monitoringPlan: '',
                kpiDefinitions: '',
                controlMechanisms: '',
                reportingPlan: '',
                complianceValidation: '',
                sustainabilityPlan: '',
                documentationStatus: '',
                effectivenessRating: '',
                implementationSummary: '',
                completed: false 
            },
            d7_prevention_plan: deviation.d7_prevention_plan || { 
                preventionSummary: '',
                keyChanges: '',
                expectedOutcome: '',
                preventionScope: '',
                resourceRequirements: '',
                detectionMethod: '',
                monitoringFrequency: '',
                alertCriteria: '',
                responseProcedures: '',
                implementationDate: '',
                projectOwner: '',
                timelineItems: [],
                riskMitigationPlan: '',
                contingencyPlans: '',
                riskMonitoring: '',
                riskCommunication: '',
                monitoringPlan: '',
                controlMechanisms: '',
                reportingStructure: '',
                escalationProcedures: '',
                successMeasures: '',
                reviewSchedule: '',
                improvementPlan: '',
                lessonsLearned: '',
                preventionActions: [],
                completed: false 
            },
            d8_team_recognition: deviation.d8_team_recognition || { 
                teamSummary: '',
                keyContributors: '',
                teamStrengths: '',
                teamMembers: [],
                criticalLessons: '',
                processImprovements: '',
                lessonsLearned: [],
                sharingPlan: '',
                documentationPlan: '',
                trainingPlan: '',
                successMetrics: '',
                impactAssessment: '',
                futurePreventionValue: '',
                celebrationPlan: '',
                communicationPlan: '',
                finalSummary: '',
                completed: false 
            }
        });
    };

    const handleCloseFullScreen = () => {
        setFullScreenOpen(false);
        setSelectedDeviation(null);
        setActiveStep(0);
        setIsReadOnly(false);
    };

    const saveInvestigationProgress = async () => {
        if (!selectedDeviation) return;

        setLoading(true);
        try {
            // Save all 8D data including:
            // D1TeamFormation: lead, leadDepartment, leadContact, members[], scope, authorityLevel, 
            //                  successCriteria, budget, timeAllocation, equipmentAccess[], customEquipment, completed
            // D2ProblemDescription: 5W2H (what, who, when, where, how, howMuch), impact assessment,
            //                       financial impact, affected products, regulatory risk, evidence, completed
            // D3InterimContainment: immediate actions, containment plan, effectiveness verification,
            //                       process/supply chain actions, monitoring/communication plan, completed
            // D4RootCauseAnalysis: 5 whys, fishbone diagram, change analysis, evidence collection,
            //                      hypotheses, root cause determination, verification plan, completed
            // D5CorrectiveActions: corrective actions array, resource allocation (budget, hours, dates),
            //                      personnel/equipment requirements, risk assessments, verification planning, completed
            // D6Implementation: strategy/planning, pilot/validation, full deployment, training/change management,
            //                   monitoring/control, compliance/sustainability, implementation actions, training programs, completed
            // D7PreventionPlan: prevention strategy, detection systems, implementation timeline, risk mitigation,
            //                   monitoring/control systems, effectiveness review, prevention actions tracking, completed
            // D8TeamRecognition: team recognition, lessons learned, knowledge sharing, success metrics,
            //                    celebration planning, team members tracking, lessons learned documentation, completed
            const updateData = {
                ...d8Data,
                status: 'investigating',
                updated_at: new Date().toISOString()
            };

            const { error } = await supabase
                .from('batch_log_deviations')
                .update(updateData)
                .eq('id', selectedDeviation.id);

            if (error) {
                console.error('Error saving investigation:', error);
                showSnackbar?.('Error saving investigation: ' + error.message, 'error');
                return;
            }

            showSnackbar?.('Investigation progress saved', 'success');
            loadPendingDeviations();
        } catch (error) {
            console.error('Error saving investigation:', error);
            showSnackbar?.('Error saving investigation: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const completeInvestigation = async () => {
        if (!selectedDeviation) return;

        setLoading(true);
        try {
            const updateData = {
                ...d8Data,
                status: 'closed_with_capa',
                investigation_completed_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            const { error } = await supabase
                .from('batch_log_deviations')
                .update(updateData)
                .eq('id', selectedDeviation.id);

            if (error) {
                console.error('Error completing investigation:', error);
                showSnackbar?.('Error completing investigation: ' + error.message, 'error');
                return;
            }

            // Immediately remove the completed deviation from pending list
            setDeviations(prevDeviations => 
                prevDeviations.filter(dev => dev.id !== selectedDeviation.id)
            );

            showSnackbar?.('Investigation completed successfully', 'success');
            loadPendingDeviations(); // Refresh to ensure consistency
            loadCompletedDeviations();
            handleCloseFullScreen();
            onInvestigationComplete?.(selectedDeviation);
        } catch (error) {
            console.error('Error completing investigation:', error);
            showSnackbar?.('Error completing investigation: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const reopenInvestigation = async () => {
        if (!selectedDeviation) return;

        setLoading(true);
        try {
            const updateData = {
                ...d8Data,
                status: 'investigating',
                investigation_completed_at: null,
                updated_at: new Date().toISOString()
            };

            const { error } = await supabase
                .from('batch_log_deviations')
                .update(updateData)
                .eq('id', selectedDeviation.id);

            if (error) {
                console.error('Error reopening investigation:', error);
                showSnackbar?.('Error reopening investigation: ' + error.message, 'error');
                return;
            }

            // Remove from completed list and add back to pending
            setCompletedDeviations(prevCompleted => 
                prevCompleted.filter(dev => dev.id !== selectedDeviation.id)
            );
            
            const reopenedDeviation = { ...selectedDeviation, status: 'investigating', investigation_completed_at: null };
            setDeviations(prevDeviations => [reopenedDeviation, ...prevDeviations]);

            showSnackbar?.('Investigation reopened successfully', 'success');
            loadPendingDeviations(); // Refresh to ensure consistency
            loadCompletedDeviations();
            onInvestigationReopened?.(reopenedDeviation); // Notify parent component
            
            // Switch to edit mode
            setIsReadOnly(false);
            setSelectedDeviation(reopenedDeviation);
        } catch (error) {
            console.error('Error reopening investigation:', error);
            showSnackbar?.('Error reopening investigation: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const areAllStepsCompleted = () => {
        return Object.values(d8Data).filter(step => step?.completed).length === 8;
    };

    const renderD8StepContent = (stepId) => {
        const stepMapping = {
            'd1': 'd1_team_formation',
            'd2': 'd2_problem_description',
            'd3': 'd3_interim_containment',
            'd4': 'd4_root_cause_analysis',
            'd5': 'd5_corrective_actions',
            'd6': 'd6_implementation',
            'd7': 'd7_prevention_plan',
            'd8': 'd8_team_recognition'
        };
        const step = d8Data[stepMapping[stepId]];
        
        const commonProps = {
            step,
            onStepUpdate: (stepKey, stepData) => {
                if (!isReadOnly && canEdit) {
                    setD8Data({
                        ...d8Data,
                        [stepKey]: stepData
                    });
                }
            },
            stepMapping,
            stepId,
            companyId,
            isReadOnly: isReadOnly || !canEdit,
            canEdit
        };
        
        switch (stepId) {
            case 'd1':
                return <D1TeamFormation {...commonProps} />;
                
            case 'd2':
                return (
                    <D2ProblemDescription 
                        {...commonProps}
                        products={products}
                        loadingProducts={loadingProducts}
                    />
                );
                
            case 'd3':
                return <D3InterimContainment {...commonProps} />;
                
            case 'd4':
                return (
                    <D4RootCauseAnalysis 
                        {...commonProps}
                        deviationId={selectedDeviation?.id}
                        batchLogId={batchLogId}
                        onAnalysisComplete={(analysisData) => {
                            setD8Data({
                                ...d8Data,
                                [stepMapping[stepId]]: { 
                                    ...analysisData,
                                    completed: true
                                }
                            });
                        }}
                    />
                );
                
            case 'd5':
                return <D5CorrectiveActions {...commonProps} />;
                
            case 'd6':
                return <D6Implementation {...commonProps} />;
                
            case 'd7':
                return <D7Prevention {...commonProps} />;
                
            case 'd8':
                return <D8Recognition {...commonProps} />;

            default:
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#ff9800' }}>
                            {d8Methodology.find(m => m.id === stepId)?.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enterprise implementation for {stepId} - To be developed in next prompt
                        </Typography>
                        <Paper sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0' }}>
                            <Typography variant="body2">
                                This step will include comprehensive forms and workflows.
                            </Typography>
                        </Paper>
                    </Box>
                );
        }
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Loading CAPA investigation system...</Typography>
                <LinearProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 2,
                pl: 3,
                pr: 3,
                maxWidth: '98vw',
                width: '100%'
            }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                                    <Box
                                        sx={{ 
                                            width: 45,
                                            height: 45,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.primary.main,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                                        }}
                                    >
                                        <PsychologyIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                            Root Cause Investigation
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            8D Methodology • Risk Assessment • Statistical Analysis
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                                        <Grid item xs={12}>
                        <Box sx={{ mb: 3 }}>
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                backdropFilter: 'blur(10px)',
                                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                                            Click the Full Screen Icon to Start Investigation
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 2 }}>
                                            Use the <FullscreenIcon sx={{ fontSize: 14, mx: 0.5, verticalAlign: 'middle', color: theme.palette.primary.main }} /> button in the Action column to open the full investigation workspace
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                        
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`, 
                            backgroundColor: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Pending Investigations ({deviations.length})
                                    </Typography>
                                </Box>
                                
                                {deviations.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <FindInPageIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                            No Pending Investigations
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            All issues have been resolved or are in review
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ 
                                        borderRadius: 3, 
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '@keyframes slideInUp': {
                                            '0%': {
                                                transform: 'translateY(100%)',
                                                opacity: 0,
                                            },
                                            '100%': {
                                                transform: 'translateY(0)',
                                                opacity: 1,
                                            }
                                        },
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                        }
                                    }}>
                                        <TableContainer sx={{ overflow: 'visible' }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Issue Type
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Description
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Severity
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            8D Progress
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Status
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Created
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                            color: 'primary.main',
                                                            py: 2,
                                                            px: 3,
                                                            width: 120
                                                        }}>
                                                            Action
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {deviations
                                                        .slice((pendingPage - 1) * 5, pendingPage * 5)
                                                        .map((deviation) => {
                                                            const isSelected = selectedDeviation?.id === deviation.id;
                                                            const completedSteps = Object.values(d8Data).filter(step => step?.completed).length;
                                                            
                                                            const getSeverityColor = (severity) => {
                                                                switch(severity?.toLowerCase()) {
                                                                    case 'critical': return '#d32f2f';
                                                                    case 'high': return '#f44336';
                                                                    case 'medium': return '#ff9800';
                                                                    case 'low': return '#4caf50';
                                                                    default: return '#9e9e9e';
                                                                }
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={deviation.id}
                                                                    hover
                                                                    onClick={() => handleDeviationSelect(deviation)}
                                                                    sx={{ 
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'white',
                                                                        borderLeft: isSelected ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                                                                        '&:hover': { 
                                                                            bgcolor: 'grey.50',
                                                                            transform: 'translateX(4px)',
                                                                            transition: 'all 0.2s ease',
                                                                            boxShadow: 'inset 4px 0 0 black'
                                                                        },
                                                                        '& .MuiTableCell-root': {
                                                                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                                            py: 2.5,
                                                                            px: 3
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body1" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.95rem'
                                                                            }}
                                                                        >
                                                                            {((deviation.issue_type?.replace(/_/g, ' ') || deviation.deviation_type || 'Unknown Issue')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' '))}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                            {(deviation.issue_description || deviation.description || 'No description').substring(0, 80)}
                                                                            {(deviation.issue_description || deviation.description || '').length > 80 && '...'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={(deviation.issue_severity || deviation.severity_level || 'Medium')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' ')}
                                                                            size="small"
                                                                            sx={{ 
                                                                                backgroundColor: getSeverityColor(deviation.issue_severity || deviation.severity_level),
                                                                                color: 'white',
                                                                                fontWeight: 500,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: 2
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', minWidth: 40, fontSize: '0.9rem' }}>
                                                                                {completedSteps}/8
                                                                            </Typography>
                                                                            <LinearProgress 
                                                                                variant="determinate" 
                                                                                value={(completedSteps / 8) * 100} 
                                                                                sx={{ 
                                                                                    height: 6, 
                                                                                    borderRadius: 3,
                                                                                    bgcolor: alpha(theme.palette.grey[300], 0.5),
                                                                                    width: 60,
                                                                                    '& .MuiLinearProgress-bar': {
                                                                                        backgroundColor: theme.palette.primary.main,
                                                                                        borderRadius: 3
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={deviation.status === 'investigating' ? 'In Progress' : 'CAPA Required'}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ 
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 500,
                                                                                borderRadius: 2
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                            {dayjs(deviation.created_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <IconButton
                                                                            size="small" 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeviationSelect(deviation);
                                                                            }}
                                                                            sx={{
                                                                                color: 'text.primary',
                                                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                                '&:hover': {
                                                                                    bgcolor: 'primary.main',
                                                                                    color: 'white',
                                                                                    transform: 'scale(1.05)',
                                                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                }
                                                                            }}
                                                                        >
                                                                            <FullscreenIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        
                                        {deviations.length > 5 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                                                {Array.from({ length: Math.ceil(deviations.length / 5) }, (_, index) => (
                                                    <Button
                                                        key={index + 1}
                                                        variant={pendingPage === index + 1 ? "contained" : "outlined"}
                                                        size="small"
                                                        onClick={() => setPendingPage(index + 1)}
                                                        sx={{
                                                            minWidth: 32,
                                                            width: 32,
                                                            height: 32,
                                                            p: 0,
                                                            borderRadius: 2,
                                                            borderColor: theme.palette.primary.main,
                                                            color: pendingPage === index + 1 ? 'white' : theme.palette.primary.main,
                                                            backgroundColor: pendingPage === index + 1 ? theme.palette.primary.main : 'transparent',
                                                            borderWidth: 1,
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            '&:hover': {
                                                                borderColor: theme.palette.primary.dark,
                                                                backgroundColor: pendingPage === index + 1 ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.05),
                                                                borderWidth: 1,
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                                            },
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Button>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Completed Investigations Table */}
                {completedDeviations.length > 0 && (
                    <Grid container spacing={3} sx={{ mt: 4 }}>
                        <Grid item xs={12}>
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                backgroundColor: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                            Completed Investigations ({completedDeviations.length})
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ 
                                        borderRadius: 3, 
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.main, 0.02)})`,
                                        boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.15)}`,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`
                                        }
                                    }}>
                                        <TableContainer sx={{ overflow: 'visible' }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Issue Type
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Description
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Severity
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Completed Date
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3,
                                                            width: 120
                                                        }}>
                                                            Action
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {completedDeviations
                                                        .slice((completedPage - 1) * 5, completedPage * 5)
                                                        .map((deviation) => {
                                                            const getSeverityColor = (severity) => {
                                                                switch(severity?.toLowerCase()) {
                                                                    case 'critical': return '#d32f2f';
                                                                    case 'high': return '#f44336';
                                                                    case 'medium': return '#ff9800';
                                                                    case 'low': return '#4caf50';
                                                                    default: return '#9e9e9e';
                                                                }
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={deviation.id}
                                                                    hover
                                                                    onClick={() => handleCompletedDeviationSelect(deviation)}
                                                                    sx={{ 
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                        backgroundColor: 'white',
                                                                        '&:hover': { 
                                                                            bgcolor: alpha(theme.palette.success.main, 0.05),
                                                                            transform: 'translateX(4px)',
                                                                            transition: 'all 0.2s ease',
                                                                            boxShadow: `inset 4px 0 0 ${theme.palette.success.main}`
                                                                        },
                                                                        '& .MuiTableCell-root': {
                                                                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                                            py: 2.5,
                                                                            px: 3
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body1" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.95rem'
                                                                            }}
                                                                        >
                                                                            {((deviation.issue_type?.replace(/_/g, ' ') || deviation.deviation_type || 'Unknown Issue')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' '))}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                            {(deviation.issue_description || deviation.description || 'No description').substring(0, 80)}
                                                                            {(deviation.issue_description || deviation.description || '').length > 80 && '...'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={(deviation.issue_severity || deviation.severity_level || 'Medium')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' ')}
                                                                            size="small"
                                                                            sx={{ 
                                                                                backgroundColor: getSeverityColor(deviation.issue_severity || deviation.severity_level),
                                                                                color: 'white',
                                                                                fontWeight: 500,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: 2
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                            {deviation.investigation_completed_at ? 
                                                                                dayjs(deviation.investigation_completed_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A') : 'Unknown'
                                                                            }
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <IconButton
                                                                            size="small" 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleCompletedDeviationSelect(deviation);
                                                                            }}
                                                                            sx={{
                                                                                color: 'text.primary',
                                                                                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                                '&:hover': {
                                                                                    bgcolor: 'success.main',
                                                                                    color: 'white',
                                                                                    transform: 'scale(1.05)',
                                                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                                                                                }
                                                                            }}
                                                                        >
                                                                            <FullscreenIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        
                                        {completedDeviations.length > 5 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                                                {Array.from({ length: Math.ceil(completedDeviations.length / 5) }, (_, index) => (
                                                    <Button
                                                        key={index + 1}
                                                        variant={completedPage === index + 1 ? "contained" : "outlined"}
                                                        size="small"
                                                        onClick={() => setCompletedPage(index + 1)}
                                                        sx={{
                                                            minWidth: 32,
                                                            width: 32,
                                                            height: 32,
                                                            p: 0,
                                                            borderRadius: 2,
                                                            borderColor: theme.palette.success.main,
                                                            color: completedPage === index + 1 ? 'white' : theme.palette.success.main,
                                                            backgroundColor: completedPage === index + 1 ? theme.palette.success.main : 'transparent',
                                                            borderWidth: 1,
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            '&:hover': {
                                                                borderColor: theme.palette.success.dark,
                                                                backgroundColor: completedPage === index + 1 ? theme.palette.success.dark : alpha(theme.palette.success.main, 0.05),
                                                                borderWidth: 1,
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`
                                                            },
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Button>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Full Screen Investigation Modal */}
                <Dialog 
                    open={fullScreenOpen} 
                    onClose={handleCloseFullScreen}
                    maxWidth={false}
                    fullWidth
                    PaperProps={{
                        sx: {
                            width: '80vw',
                            height: '100vh',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            m: 1,
                            transform: 'scale(0.90)',
                            transformOrigin: 'center center',
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        backgroundColor: isReadOnly 
                            ? alpha(theme.palette.success.main, 0.05) 
                            : alpha(theme.palette.primary.main, 0.05), 
                        color: theme.palette.text.primary, 
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PsychologyIcon sx={{ 
                                fontSize: 24, 
                                color: isReadOnly ? theme.palette.success.main : theme.palette.primary.main 
                            }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Investigation: {selectedDeviation?.id} - 8D Root Cause Analysis
                                </Typography>
                                {isReadOnly && (
                                    <Chip 
                                        label="COMPLETED - READ ONLY" 
                                        size="small"
                                        sx={{ 
                                            backgroundColor: theme.palette.success.main,
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            mt: 0.5
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <IconButton
                            onClick={handleCloseFullScreen}
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                color: theme.palette.grey[600],
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
                        <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            backgroundColor: '#f8fafc'
                        }}>
                            {/* Investigation Summary Bar */}
                            <Box sx={{ 
                                backgroundColor: 'white', 
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                p: 3,
                                flexShrink: 0
                            }}>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            {(selectedDeviation?.issue_type?.replace(/_/g, ' ') || selectedDeviation?.deviation_type || 'Investigation')
                                                .split(' ')
                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(' ')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {(selectedDeviation?.issue_description || selectedDeviation?.description || '').substring(0, 100)}...
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
                                            <Chip 
                                                label={isReadOnly 
                                                    ? `Completed: 8/8 Steps` 
                                                    : `Progress: ${Object.values(d8Data).filter(step => step?.completed).length}/8 Steps`
                                                }
                                                sx={{ 
                                                    backgroundColor: isReadOnly 
                                                        ? theme.palette.success.main 
                                                        : theme.palette.primary.main,
                                                    color: 'white',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Main Content Area */}
                            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }} data-scroll-container="investigation-content">
                                {/* 8D Methodology Content */}
                                    <Box sx={{ 
                                        backgroundColor: 'white', 
                                        borderRadius: 4,
                                        border: `1px solid ${theme.palette.grey[400]}`,
                                        backdropFilter: 'blur(20px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        p: 4,
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}>
                                        <Stepper 
                                            activeStep={activeStep} 
                                            orientation="vertical"
                                            sx={{
                                                '& .MuiStepConnector-line': {
                                                    borderLeftColor: '#d1d5db',
                                                    borderLeftWidth: '2px'
                                                }
                                            }}
                                        >
                                            {d8Methodology.map((step, index) => (
                                                <Step key={step.id}>
                                                    <StepLabel 
                                                        onClick={() => {
                                                            setActiveStep(index);
                                                            // Smooth scroll to top of content area
                                                            const contentArea = document.querySelector('[data-scroll-container="investigation-content"]');
                                                            if (contentArea) {
                                                                contentArea.scrollTo({ top: 0, behavior: 'smooth' });
                                                            }
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                        StepIconComponent={({ active }) => {
                                                            const stepMapping = {
                                                                'd1': 'd1_team_formation',
                                                                'd2': 'd2_problem_description',
                                                                'd3': 'd3_interim_containment',
                                                                'd4': 'd4_root_cause_analysis',
                                                                'd5': 'd5_corrective_actions',
                                                                'd6': 'd6_implementation',
                                                                'd7': 'd7_prevention_plan',
                                                                'd8': 'd8_team_recognition'
                                                            };
                                                            const isCompleted = d8Data[stepMapping[step.id]]?.completed || false;
                                                            
                                                            return (
                                                                <Avatar 
                                                                    sx={{ 
                                                                        backgroundColor: isCompleted ? '#22c55e' : active ? '#9333ea' : '#9e9e9e',
                                                                        width: 56, 
                                                                        height: 56,
                                                                        cursor: 'pointer',
                                                                        border: isCompleted ? '3px solid #16a34a' : active ? '3px solid #9333ea' : '3px solid #9e9e9e',
                                                                        boxShadow: active || isCompleted ? '0 6px 16px rgba(0,0,0,0.2)' : 'none'
                                                                    }}
                                                                >
                                                                    {isCompleted ? (
                                                                        <CheckIcon sx={{ 
                                                                            color: 'white',
                                                                            fontSize: '1.8rem'
                                                                        }} />
                                                                    ) : (
                                                                        React.cloneElement(step.icon, { 
                                                                            sx: { 
                                                                                color: 'white',
                                                                                fontSize: '1.8rem'
                                                                            } 
                                                                        })
                                                                    )}
                                                                </Avatar>
                                                            );
                                                        }}
                                                    >
                                                        <Typography 
                                                            variant="h5" 
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                color: theme.palette.primary.main,
                                                                fontWeight: 500,
                                                                mb: 1
                                                            }}
                                                        >
                                                            {step.title}
                                                        </Typography>
                                                        <Typography 
                                                            variant="body1" 
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {step.description}
                                                        </Typography>
                                                    </StepLabel>
                                                    <StepContent>
                                                        <Box sx={{ mt: 3 }}>
                                                            {renderD8StepContent(step.id)}
                                                        </Box>
                                                        <Box sx={{ mt: 4, mb: 2 }}>
                                                                                                                    <Button
                                                            variant="contained"
                                                            onClick={() => {
                                                                setActiveStep(activeStep + 1);
                                                                // Smooth scroll to top of content area
                                                                const contentArea = document.querySelector('[data-scroll-container="investigation-content"]');
                                                                if (contentArea) {
                                                                    contentArea.scrollTo({ top: 0, behavior: 'smooth' });
                                                                }
                                                            }}
                                                            sx={{ 
                                                                mr: 2,
                                                                borderRadius: 2,
                                                                px: 4,
                                                                py: 1.5,
                                                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                                fontSize: '1rem',
                                                                '&:hover': {
                                                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                                                },
                                                                '&.Mui-disabled': {
                                                                    opacity: 0.3,
                                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                                                    color: 'white',
                                                                    boxShadow: 'none',
                                                                    transform: 'none'
                                                                },
                                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                            }}
                                                            disabled={index === d8Methodology.length - 1}
                                                        >
                                                            Next Step
                                                        </Button>
                                                        <Button
                                                            disabled={index === 0}
                                                            onClick={() => {
                                                                setActiveStep(activeStep - 1);
                                                                // Smooth scroll to top of content area
                                                                const contentArea = document.querySelector('[data-scroll-container="investigation-content"]');
                                                                if (contentArea) {
                                                                    contentArea.scrollTo({ top: 0, behavior: 'smooth' });
                                                                }
                                                            }}
                                                            sx={{ 
                                                                borderRadius: 2,
                                                                px: 4,
                                                                py: 1.5,
                                                                borderColor: theme.palette.primary.main,
                                                                color: theme.palette.primary.main,
                                                                fontWeight: 500,
                                                                fontSize: '1rem',
                                                                '&:hover': {
                                                                    borderColor: theme.palette.primary.main,
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                                }
                                                            }}
                                                            variant="outlined"
                                                        >
                                                            Previous Step
                                                        </Button>
                                                        </Box>
                                                    </StepContent>
                                                </Step>
                                            ))}
                                        </Stepper>
                                    </Box>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ 
                        p: 3, 
                        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        backgroundColor: 'white'
                    }}>
                        {isReadOnly ? (
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={reopenInvestigation}
                                disabled={!canCloseInvestigations || loading}
                                sx={{ 
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    background: canCloseInvestigations ? `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})` : theme.palette.grey[400],
                                    color: canCloseInvestigations ? 'white' : theme.palette.grey[100],
                                    fontWeight: 500,
                                    boxShadow: canCloseInvestigations ? `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}` : 'none',
                                    '&:hover': {
                                        background: canCloseInvestigations ? `linear-gradient(135deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})` : theme.palette.grey[400],
                                        transform: canCloseInvestigations ? 'translateY(-1px)' : 'none',
                                        boxShadow: canCloseInvestigations ? `0 6px 20px ${alpha(theme.palette.warning.main, 0.4)}` : 'none'
                                    },
                                    '&:disabled': {
                                        background: theme.palette.grey[300],
                                        color: theme.palette.grey[500]
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                {canCloseInvestigations ? 'Reopen Investigation' : 'No Close Investigation Permissions'}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<SaveIcon />}
                                    onClick={saveInvestigationProgress}
                                    disabled={!canEdit || loading}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1.5,
                                        borderColor: canEdit ? theme.palette.primary.main : theme.palette.grey[400],
                                        color: canEdit ? theme.palette.primary.main : theme.palette.grey[500],
                                        fontWeight: 500,
                                        '&:hover': {
                                            borderColor: canEdit ? theme.palette.primary.main : theme.palette.grey[400],
                                            backgroundColor: canEdit ? alpha(theme.palette.primary.main, 0.05) : 'transparent'
                                        },
                                        '&:disabled': {
                                            borderColor: theme.palette.grey[400],
                                            color: theme.palette.grey[500]
                                        }
                                    }}
                                >
                                    {canEdit ? 'Save Progress' : 'Read Only - No Edit Permissions'}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={completeInvestigation}
                                    disabled={!canCloseInvestigations || loading}
                                    sx={{ 
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        background: canCloseInvestigations 
                                            ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                            : theme.palette.grey[300],
                                        color: canCloseInvestigations ? 'white' : theme.palette.grey[500],
                                        fontWeight: 500,
                                        boxShadow: canCloseInvestigations 
                                            ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                                            : 'none',
                                        '&:hover': canCloseInvestigations ? {
                                            background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                                        } : {},
                                        '&:disabled': {
                                            background: theme.palette.grey[300],
                                            color: theme.palette.grey[500]
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    {!canCloseInvestigations ? 'No Close Investigation Permissions' : 'Complete Investigation'}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
} 